import { DocumentStatus, type Document } from "~/api/model";
import type { DocumentWithBates, FolderWithBates } from "~/types/bates";
import { useParentCase } from "./useParentCase";

/**
 * A document can receive a Bates stamp when it is fully processed and not
 * already stamped or explicitly skipped (a detection hint is overridable).
 */
export function isBatesEligible(document: Document): boolean {
  const status = (document as DocumentWithBates).batesStampingStatus;
  return (
    document.status === DocumentStatus.COMPLETE &&
    (!status || status === "NONE" || status === "DETECTED_EXISTING")
  );
}

/**
 * A document is mid-stamping while the restamp lambda re-renders its page
 * images. The pages may still carry pre-stamp overlays during this window, so
 * such a document must not be treated as ready for version/copy flows.
 */
export function isDocumentStamping(document: Document): boolean {
  return (document as DocumentWithBates).batesStampingStatus === "STAMPING";
}

/**
 * A document is mid-unstamping while the unstamp lambda restores its clean page
 * images. As with stamping, the page images are in flux, so the document must
 * not be treated as ready for version/copy flows during this window.
 */
export function isDocumentUnstamping(document: Document): boolean {
  return (document as DocumentWithBates).batesStampingStatus === "UNSTAMPING";
}

/**
 * A document's page images are in flux whenever it is being stamped or
 * unstamped. In either state the document must not be treated as ready for
 * version/copy flows, since the pages may carry partial overlays mid-operation.
 */
export function isDocumentBatesInFlight(document: Document): boolean {
  return isDocumentStamping(document) || isDocumentUnstamping(document);
}

/**
 * Bates numbers can be removed from a document only once it has been fully
 * stamped (mirrors the remove-bates endpoint, which claims STAMPED documents).
 */
export function isBatesRemovable(document: Document): boolean {
  return (document as DocumentWithBates).batesStampingStatus === "STAMPED";
}

/**
 * Whether Bates numbering applies to the current case: only after the master
 * case has loaded, and only when that case is not explicitly disabled.
 */
export function useIsBatesEnabled(): boolean {
  const { project: masterCase } = useParentCase();

  if (!masterCase) {
    return false;
  }

  const caseDisabled =
    (masterCase as FolderWithBates | undefined)?.batesNumberingDisabled ===
    true;

  return !caseDisabled;
}
